import {
    KNOWN_TEAM_CODES,
    TeamError,
    type CreateTeamInput,
    type ProviderQuota,
    type ProviderQuotaUsage,
    type QuotaDimensionUsage,
    type Team,
    type TeamErrorCode,
    type TeamMember,
    type UpdateTeamInput,
} from '@/domain/teams'
import type { TeamsApi } from '@/ports/teams-api'

import { appendActiveTenant } from './_active-tenant'

export class HttpTeamsApi implements TeamsApi {
    private readonly baseURL: string

    constructor(baseURL = '') {
        this.baseURL = baseURL.replace(/\/+$/, '')
    }

    async list(tenantId?: string): Promise<Team[]> {
        // Sans tenant explicite : applique l'override super_admin du topbar
        // (sinon un super_admin liste son propre tenant, pas celui affiché).
        const params = new URLSearchParams()
        if (tenantId) params.set('tenant_id', tenantId)
        else appendActiveTenant(params)
        const qs = params.toString()
        const resp = await this.send(qs ? `/api/v1/teams?${qs}` : '/api/v1/teams', { method: 'GET' })
        if (!resp.ok) throw await readError(resp)
        const body = (await resp.json()) as { items?: unknown }
        if (!Array.isArray(body.items)) {
            throw new TeamError('internal_error', 'Invalid /teams response shape')
        }
        return body.items.map(parseTeam)
    }

    async create(input: CreateTeamInput, csrfToken: string): Promise<Team> {
        const payload: Record<string, unknown> = { name: input.name }
        if (input.description) payload.description = input.description
        if (input.tenantId) payload.tenant_id = input.tenantId
        const resp = await this.send('/api/v1/teams', {
            method: 'POST',
            headers: csrfHeaders(csrfToken),
            body: JSON.stringify(payload),
        })
        if (!resp.ok) throw await readError(resp)
        const body = (await resp.json()) as { team?: unknown }
        return parseTeam(body.team)
    }

    async update(id: string, input: UpdateTeamInput, csrfToken: string): Promise<Team> {
        const resp = await this.send(`/api/v1/teams/${encodeURIComponent(id)}`, {
            method: 'PATCH',
            headers: csrfHeaders(csrfToken),
            body: JSON.stringify({ name: input.name, description: input.description }),
        })
        if (!resp.ok) throw await readError(resp)
        const body = (await resp.json()) as { team?: unknown }
        return parseTeam(body.team)
    }

    async remove(id: string, csrfToken: string): Promise<void> {
        const resp = await this.send(`/api/v1/teams/${encodeURIComponent(id)}`, {
            method: 'DELETE',
            headers: csrfHeaders(csrfToken),
        })
        if (!resp.ok) throw await readError(resp)
    }

    async listMembers(teamId: string): Promise<TeamMember[]> {
        const resp = await this.send(`/api/v1/teams/${encodeURIComponent(teamId)}/members`, {
            method: 'GET',
        })
        if (!resp.ok) throw await readError(resp)
        const body = (await resp.json()) as { items?: unknown }
        if (!Array.isArray(body.items)) {
            throw new TeamError('internal_error', 'Invalid /members response shape')
        }
        return body.items.map(parseMember)
    }

    async addMember(teamId: string, userId: string, csrfToken: string): Promise<void> {
        const resp = await this.send(`/api/v1/teams/${encodeURIComponent(teamId)}/members`, {
            method: 'POST',
            headers: csrfHeaders(csrfToken),
            body: JSON.stringify({ user_id: userId }),
        })
        if (!resp.ok) throw await readError(resp)
    }

    async removeMember(teamId: string, userId: string, csrfToken: string): Promise<void> {
        const resp = await this.send(
            `/api/v1/teams/${encodeURIComponent(teamId)}/members/${encodeURIComponent(userId)}`,
            { method: 'DELETE', headers: csrfHeaders(csrfToken) },
        )
        if (!resp.ok) throw await readError(resp)
    }

    async setRoute(teamId: string, routeId: string, csrfToken: string): Promise<Team> {
        const resp = await this.send(`/api/v1/teams/${encodeURIComponent(teamId)}/route`, {
            method: 'PATCH',
            headers: csrfHeaders(csrfToken),
            body: JSON.stringify({ route_id: routeId }),
        })
        if (!resp.ok) throw await readError(resp)
        const body = (await resp.json()) as { team?: unknown }
        return parseTeam(body.team)
    }

    async applyRoute(teamId: string, apiKeyIds: string[], csrfToken: string): Promise<number> {
        const resp = await this.send(`/api/v1/teams/${encodeURIComponent(teamId)}/apply-route`, {
            method: 'POST',
            headers: csrfHeaders(csrfToken),
            body: JSON.stringify({ api_key_ids: apiKeyIds }),
        })
        if (!resp.ok) throw await readError(resp)
        const body = (await resp.json()) as { applied?: unknown }
        return typeof body.applied === 'number' ? body.applied : 0
    }

    async setProviderQuotas(
        teamId: string,
        quotas: Record<string, ProviderQuota>,
        csrfToken: string,
    ): Promise<Team> {
        const payload: Record<string, { tokens_monthly?: number; eur_monthly?: number }> = {}
        for (const [pkId, q] of Object.entries(quotas)) {
            payload[pkId] = {
                ...(q.tokensMonthly > 0 ? { tokens_monthly: q.tokensMonthly } : {}),
                ...(q.eurMonthly > 0 ? { eur_monthly: q.eurMonthly } : {}),
            }
        }
        const resp = await this.send(
            `/api/v1/teams/${encodeURIComponent(teamId)}/provider-quotas`,
            {
                method: 'PUT',
                headers: csrfHeaders(csrfToken),
                body: JSON.stringify({ provider_quotas: payload }),
            },
        )
        if (!resp.ok) throw await readError(resp)
        const body = (await resp.json()) as { team?: unknown }
        return parseTeam(body.team)
    }

    async getProviderQuotasUsage(teamId: string): Promise<ProviderQuotaUsage[]> {
        const resp = await this.send(
            `/api/v1/teams/${encodeURIComponent(teamId)}/provider-quotas/usage`,
            { method: 'GET' },
        )
        if (!resp.ok) throw await readError(resp)
        const body = (await resp.json()) as { items?: unknown }
        if (!Array.isArray(body.items)) return []
        return body.items.map(parseProviderQuotaUsage)
    }

    private send(path: string, init: RequestInit): Promise<Response> {
        return fetch(this.baseURL + path, {
            ...init,
            credentials: 'include',
            cache: 'no-store',
        }).catch((cause: unknown) => {
            const message = cause instanceof Error ? cause.message : 'fetch failed'
            throw new TeamError('network_error', message)
        })
    }
}

function csrfHeaders(token: string): Record<string, string> {
    return { 'Content-Type': 'application/json', 'X-CSRF-Token': token }
}

function parseProviderQuotaUsage(raw: unknown): ProviderQuotaUsage {
    if (!raw || typeof raw !== 'object') {
        throw new TeamError('internal_error', 'Invalid provider quota usage payload')
    }
    const obj = raw as Record<string, unknown>
    return {
        providerKeyId: stringField(obj.provider_key_id),
        tokens: parseQuotaDimension(obj.tokens),
        eur: parseQuotaDimension(obj.eur),
    }
}

function parseQuotaDimension(raw: unknown): QuotaDimensionUsage {
    const obj = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>
    return {
        used: numberField(obj.used),
        limit: numberField(obj.limit),
        state: stringField(obj.state),
    }
}

function parseTeam(raw: unknown): Team {
    if (!raw || typeof raw !== 'object') {
        throw new TeamError('internal_error', 'Invalid team payload')
    }
    const obj = raw as Record<string, unknown>
    return {
        id: stringField(obj.id),
        tenantId: stringField(obj.tenant_id),
        name: stringField(obj.name),
        description: stringField(obj.description),
        routeId: stringField(obj.route_id) || undefined,
        providerQuotas: parseProviderQuotas(obj.provider_quotas),
        createdAt: stringField(obj.created_at),
    }
}

function parseProviderQuotas(raw: unknown): Record<string, ProviderQuota> | undefined {
    if (!raw || typeof raw !== 'object') return undefined
    const out: Record<string, ProviderQuota> = {}
    for (const [pkId, v] of Object.entries(raw as Record<string, unknown>)) {
        if (!v || typeof v !== 'object') continue
        const q = v as Record<string, unknown>
        out[pkId] = {
            tokensMonthly: typeof q.tokens_monthly === 'number' ? q.tokens_monthly : 0,
            eurMonthly: typeof q.eur_monthly === 'number' ? q.eur_monthly : 0,
        }
    }
    return Object.keys(out).length > 0 ? out : undefined
}

function parseMember(raw: unknown): TeamMember {
    if (!raw || typeof raw !== 'object') {
        throw new TeamError('internal_error', 'Invalid member payload')
    }
    const obj = raw as Record<string, unknown>
    return {
        id: stringField(obj.id),
        email: stringField(obj.email),
        name: stringField(obj.name),
        role: stringField(obj.role),
        disabled: typeof obj.disabled === 'boolean' ? obj.disabled : false,
    }
}

function stringField(v: unknown): string {
    return typeof v === 'string' ? v : ''
}

function numberField(v: unknown): number {
    return typeof v === 'number' && Number.isFinite(v) ? v : 0
}

async function readError(resp: Response): Promise<TeamError> {
    if (resp.status === 401) {
        return new TeamError('unauthorized', 'Session expired', 401)
    }
    if (resp.status === 403) {
        try {
            const body = (await resp.json()) as { error?: { code?: unknown } }
            const code = typeof body.error?.code === 'string' ? body.error.code : ''
            if (code === 'csrf_required' || code === 'csrf_invalid' || code === 'tenant_scope') {
                return new TeamError(code as TeamErrorCode, code, 403)
            }
        } catch {
            // fallback
        }
        return new TeamError('forbidden', 'Forbidden', 403)
    }
    let body: unknown = null
    try {
        body = await resp.json()
    } catch {
        return new TeamError('unknown', `HTTP ${resp.status}`, resp.status)
    }
    const err = (body as { error?: { code?: unknown; message?: unknown } }).error
    if (!err || typeof err !== 'object') {
        return new TeamError('unknown', `HTTP ${resp.status}`, resp.status)
    }
    const rawCode = typeof err.code === 'string' ? err.code : 'unknown'
    const code: TeamErrorCode = (KNOWN_TEAM_CODES as readonly string[]).includes(rawCode)
        ? (rawCode as TeamErrorCode)
        : 'unknown'
    const message = typeof err.message === 'string' ? err.message : `HTTP ${resp.status}`
    return new TeamError(code, message, resp.status)
}
